import { useState, useEffect, useCallback, useMemo } from 'react';
import { s, authHeaders, Toast, StandardDataGrid, gridActionButton } from './listPageStyles';
import { DateInput } from './reports/DateInput';

const API_BASE = '/api/cooler-fault-reports';

const STATUS_TABS = [
  { key: '', label: 'Tümü' },
  { key: 'Open', label: 'Açık' },
  { key: 'InProgress', label: 'Devam' },
  { key: 'Resolved', label: 'Çözüldü' },
  { key: 'Cancelled', label: 'İptal' },
];

const STATUS_META = {
  Open: { label: 'Açık', tone: 'warn' },
  InProgress: { label: 'Devam', tone: 'warn' },
  Resolved: { label: 'Çözüldü', tone: 'ok' },
  Cancelled: { label: 'İptal', tone: 'neutral' },
};

const URGENCY_TABS = [
  { key: '', label: 'Tüm Öncelikler' },
  { key: 'CRITICAL', label: 'Kritik' },
  { key: 'HIGH', label: 'Yüksek' },
  { key: 'NORMAL', label: 'Normal' },
  { key: 'LOW', label: 'Düşük' },
];

const URGENCY_META = {
  CRITICAL: { label: 'Kritik', tone: 'bad' },
  HIGH: { label: 'Yüksek', tone: 'warn' },
  NORMAL: { label: 'Normal', tone: 'neutral' },
  LOW: { label: 'Düşük', tone: 'neutral' },
};

const REASON_LABEL = {
  COMPRESSOR: 'Kompresör',
  DOOR: 'Kapı',
  LEAK: 'Sızıntı',
  ELECTRICAL: 'Elektrik',
  NOT_COOLING: 'Soğutmuyor',
  OTHER: 'Diğer',
};

const PAGE_SIZE_DEFAULT = 20;

function pick(obj, ...keys) {
  if (!obj) return undefined;
  for (const k of keys) {
    if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  }
  return undefined;
}

function unwrapResponse(payload) {
  const data = pick(payload, 'Data', 'data') ?? payload;
  return {
    items: pick(data, 'items', 'Items') ?? [],
    openCount: pick(data, 'openCount', 'OpenCount') ?? 0,
    inProgressCount: pick(data, 'inProgressCount', 'InProgressCount') ?? 0,
    resolvedCount: pick(data, 'resolvedCount', 'ResolvedCount') ?? 0,
    cancelledCount: pick(data, 'cancelledCount', 'CancelledCount') ?? 0,
    criticalOpenCount: pick(data, 'criticalOpenCount', 'CriticalOpenCount') ?? 0,
  };
}

function pillFor(meta) {
  const tones = {
    ok: { color: '#0f5132', background: 'rgba(0, 200, 117, 0.12)', borderColor: 'rgba(0, 200, 117, 0.30)' },
    warn: { color: '#92400e', background: 'rgba(253, 171, 61, 0.14)', borderColor: 'rgba(253, 171, 61, 0.32)' },
    bad: { color: '#7f1d1d', background: 'rgba(226, 68, 92, 0.12)', borderColor: 'rgba(226, 68, 92, 0.30)' },
    neutral: { color: '#64748b', background: 'rgba(100, 116, 139, 0.14)', borderColor: 'rgba(100, 116, 139, 0.28)' },
  };
  const t = tones[meta.tone];
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '4px 10px', borderRadius: 999, fontWeight: 800, fontSize: 12,
      border: `1px solid ${t.borderColor}`, background: t.background, color: t.color,
      whiteSpace: 'nowrap',
    }}>
      <span style={{ width: 7, height: 7, borderRadius: 999, background: 'currentColor', opacity: 0.85 }} />
      {meta.label}
    </span>
  );
}

function statusPillFor(status) {
  return pillFor(STATUS_META[status] || { label: status || '-', tone: 'neutral' });
}

function urgencyPillFor(urgency) {
  return pillFor(URGENCY_META[urgency] || { label: urgency || '-', tone: 'neutral' });
}

function formatDate(value) {
  if (!value) return '-';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '-';
    return d.toLocaleString('tr-TR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return '-'; }
}

function toIsoStart(d) { if (!d) return ''; try { return new Date(`${d}T00:00:00`).toISOString(); } catch { return ''; } }
function toIsoEnd(d) {
  if (!d) return '';
  try { const x = new Date(`${d}T00:00:00`); x.setDate(x.getDate() + 1); return x.toISOString(); } catch { return ''; }
}

function parsePhotoUrls(json) {
  if (!json) return [];
  try {
    const parsed = JSON.parse(json);
    return Array.isArray(parsed) ? parsed : [];
  } catch { return []; }
}

function badge(label, value, tone) {
  const tones = {
    blue: { bg: 'rgba(0, 115, 234, 0.10)', border: 'rgba(0, 115, 234, 0.32)', color: '#1d4ed8', dot: 'var(--accent)' },
    green: { bg: 'rgba(0, 200, 117, 0.12)', border: 'rgba(0, 200, 117, 0.32)', color: '#0f5132', dot: '#16a34a' },
    orange: { bg: 'rgba(253, 171, 61, 0.14)', border: 'rgba(253, 171, 61, 0.32)', color: '#92400e', dot: 'var(--warn)' },
    red: { bg: 'rgba(226, 68, 92, 0.12)', border: 'rgba(226, 68, 92, 0.30)', color: '#7f1d1d', dot: '#dc2626' },
    gray: { bg: 'rgba(100, 116, 139, 0.10)', border: 'rgba(100, 116, 139, 0.28)', color: '#475569', dot: '#64748b' },
  };
  const t = tones[tone] || tones.blue;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 8,
      padding: '7px 12px', borderRadius: 999,
      background: t.bg, border: `1px solid ${t.border}`, color: t.color,
      fontWeight: 800, fontSize: 12,
    }}>
      <span style={{ width: 7, height: 7, borderRadius: 999, background: t.dot }} />
      {label}: {value}
    </span>
  );
}

export function CoolerFaultsPageLegacy() {
  const [statusFilter, setStatusFilter] = useState('');
  const [urgencyFilter, setUrgencyFilter] = useState('');
  const [barcodeFilter, setBarcodeFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [searchText, setSearchText] = useState('');

  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ openCount: 0, inProgressCount: 0, resolvedCount: 0, cancelledCount: 0, criticalOpenCount: 0 });
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_DEFAULT);
  const [sortField, setSortField] = useState('ReportedAt');
  const [sortDir, setSortDir] = useState('desc');
  const [selectedId, setSelectedId] = useState(null);

  const [detailRow, setDetailRow] = useState(null);
  const [editTrackingNo, setEditTrackingNo] = useState('');
  const [editStatus, setEditStatus] = useState('');
  const [saving, setSaving] = useState(false);

  const showToast = useCallback((type, message) => {
    setToast({ type, message });
    window.setTimeout(() => setToast(null), 3000);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      if (urgencyFilter) params.set('urgency', urgencyFilter);
      if (barcodeFilter.trim()) params.set('coolerBarcode', barcodeFilter.trim());
      const isoFrom = toIsoStart(dateFrom);
      const isoTo = toIsoEnd(dateTo);
      if (isoFrom) params.set('from', isoFrom);
      if (isoTo) params.set('to', isoTo);
      params.set('take', '500');

      const res = await fetch(`${API_BASE}?${params.toString()}`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Liste alınamadı (${res.status})`);
      const payload = await res.json();
      const data = unwrapResponse(payload);
      setRows(Array.isArray(data.items) ? data.items : []);
      setCounts({
        openCount: data.openCount,
        inProgressCount: data.inProgressCount,
        resolvedCount: data.resolvedCount,
        cancelledCount: data.cancelledCount,
        criticalOpenCount: data.criticalOpenCount,
      });
      setPage(1);
    } catch (err) {
      showToast('error', err.message || 'Liste alınamadı');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, urgencyFilter, barcodeFilter, dateFrom, dateTo, showToast]);

  useEffect(() => { load(); }, [load]);

  const openDetail = (row) => {
    setDetailRow(row);
    setEditTrackingNo(pick(row, 'trackingNo', 'TrackingNo') || '');
    setEditStatus(pick(row, 'status', 'Status') || 'Open');
  };

  const closeDetail = () => { setDetailRow(null); setEditTrackingNo(''); setEditStatus(''); };

  const submitUpdate = async () => {
    if (!detailRow) return;
    const id = pick(detailRow, 'id', 'Id');
    setSaving(true);
    try {
      const body = {
        trackingNo: editTrackingNo.trim() || null,
        status: editStatus,
      };
      const res = await fetch(`${API_BASE}/${id}`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify(body),
      });
      const payload = await res.json().catch(() => null);
      if (!res.ok) {
        const msg = pick(payload, 'Message', 'message') || `İşlem başarısız (${res.status})`;
        throw new Error(msg);
      }
      showToast('success', 'Bildirim güncellendi.');
      closeDetail();
      await load();
    } catch (err) {
      showToast('error', err.message || 'İşlem başarısız');
    } finally {
      setSaving(false);
    }
  };

  const filteredSorted = useMemo(() => {
    const needle = searchText.trim().toLocaleLowerCase('tr-TR');
    const match = (row) => {
      if (!needle) return true;
      const fields = [
        pick(row, 'coolerBarcode', 'CoolerBarcode'),
        pick(row, 'customerTitle', 'CustomerTitle'),
        pick(row, 'note', 'Note'),
        pick(row, 'trackingNo', 'TrackingNo'),
      ];
      return fields.some(v => v && String(v).toLocaleLowerCase('tr-TR').includes(needle));
    };
    const filtered = rows.filter(match);
    const dir = sortDir === 'desc' ? -1 : 1;
    const sortKey = (row) => {
      switch (sortField) {
        case 'CoolerBarcode': return pick(row, 'coolerBarcode', 'CoolerBarcode') || '';
        case 'CustomerTitle': return pick(row, 'customerTitle', 'CustomerTitle') || '';
        case 'Urgency': {
          // CRITICAL=4 > HIGH=3 > NORMAL=2 > LOW=1
          const rank = { CRITICAL: 4, HIGH: 3, NORMAL: 2, LOW: 1 };
          return rank[pick(row, 'urgency', 'Urgency')] ?? 0;
        }
        case 'Status': return pick(row, 'status', 'Status') || '';
        case 'ReportedAt':
        default: {
          const v = pick(row, 'reportedAt', 'ReportedAt');
          return v ? new Date(v).getTime() : 0;
        }
      }
    };
    return [...filtered].sort((a, b) => {
      const av = sortKey(a);
      const bv = sortKey(b);
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [rows, searchText, sortField, sortDir]);

  const pagedRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredSorted.slice(start, start + pageSize);
  }, [filteredSorted, page, pageSize]);

  const columns = useMemo(() => [
    {
      field: 'reportedAt',
      headerName: 'Bildirim',
      flex: 0.9,
      minWidth: 150,
      valueGetter: ({ row }) => formatDate(pick(row, 'reportedAt', 'ReportedAt')),
    },
    {
      field: 'coolerBarcode',
      headerName: 'Cihaz',
      flex: 0.9,
      minWidth: 140,
      renderCell: ({ row }) => (
        <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3 }}>
          <span style={{ fontWeight: 700, fontFamily: 'monospace', fontSize: 13 }}>
            {pick(row, 'coolerBarcode', 'CoolerBarcode') || '-'}
          </span>
          {pick(row, 'affectedDoor', 'AffectedDoor') != null && (
            <span style={{ fontSize: 11, color: '#6b7280' }}>
              Kapı #{pick(row, 'affectedDoor', 'AffectedDoor')}
            </span>
          )}
        </div>
      ),
    },
    {
      field: 'customerTitle',
      headerName: 'Nokta',
      flex: 1.2,
      minWidth: 170,
      valueGetter: ({ row }) => pick(row, 'customerTitle', 'CustomerTitle') || '-',
    },
    {
      field: 'reason',
      headerName: 'Sebep',
      flex: 0.7,
      minWidth: 120,
      valueGetter: ({ row }) => {
        const r = pick(row, 'reason', 'Reason');
        return REASON_LABEL[r] || r || '-';
      },
    },
    {
      field: 'urgency',
      headerName: 'Öncelik',
      flex: 0.6,
      minWidth: 110,
      renderCell: ({ row }) => urgencyPillFor(pick(row, 'urgency', 'Urgency')),
    },
    {
      field: 'trackingNo',
      headerName: 'Takip No',
      flex: 0.8,
      minWidth: 130,
      renderCell: ({ row }) => {
        const tn = pick(row, 'trackingNo', 'TrackingNo');
        return tn
          ? <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{tn}</span>
          : <span style={{ color: '#cbd5e1' }}>(atanmamış)</span>;
      },
    },
    {
      field: 'status',
      headerName: 'Durum',
      flex: 0.7,
      minWidth: 120,
      renderCell: ({ row }) => statusPillFor(pick(row, 'status', 'Status')),
    },
    {
      field: 'actions',
      headerName: 'Aksiyon',
      flex: 0.7,
      minWidth: 130,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => (
        <div onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            style={{ ...gridActionButton, background: 'var(--accent)' }}
            onClick={() => openDetail(row)}
          >
            Yönet
          </button>
        </div>
      ),
    },
  ], []);

  const totalCount = filteredSorted.length;
  const photoUrls = detailRow ? parsePhotoUrls(pick(detailRow, 'photoUrlsJson', 'PhotoUrlsJson')) : [];

  return (
    <div style={s.container}>
      <div style={s.pageHeader}>
        <div style={s.pageHeaderText}>
          <h1 style={s.pageTitle}>Soğutucu Arızaları</h1>
          <p style={s.pageSubtitle}>
            Saha temsilcisinin mobile'dan açtığı soğutucu arıza bildirimleri. Servis takip
            no atayın, durum güncelleyin. Yeni kayıt sadece mobile push ile gelir.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          {counts.criticalOpenCount > 0 && badge('Kritik+Açık', counts.criticalOpenCount, 'red')}
          {badge('Açık', counts.openCount, 'orange')}
          {badge('Devam', counts.inProgressCount, 'blue')}
          {badge('Çözüldü', counts.resolvedCount, 'green')}
          {badge('İptal', counts.cancelledCount, 'gray')}
          <button type="button" style={s.btnSecondary} onClick={load} disabled={loading}>
            {loading ? 'Yükleniyor...' : 'Yenile'}
          </button>
        </div>
      </div>

      <div style={s.toolbar}>
        <div style={s.toolbarLeft}>
          {STATUS_TABS.map(tab => (
            <button
              key={tab.key || 'all'}
              type="button"
              onClick={() => setStatusFilter(tab.key)}
              style={{
                ...(statusFilter === tab.key ? s.btnPrimary : s.btnSecondary),
                padding: '6px 14px', fontSize: 13,
              }}
            >
              {tab.label}
            </button>
          ))}
          <span style={{ width: 1, height: 22, background: '#e5e7eb', margin: '0 4px' }} />
          <select
            value={urgencyFilter}
            onChange={(e) => setUrgencyFilter(e.target.value)}
            style={s.select}
          >
            {URGENCY_TABS.map(t => <option key={t.key || 'all'} value={t.key}>{t.label}</option>)}
          </select>
        </div>
        <div style={s.toolbarRight}>
          <input
            type="text"
            value={barcodeFilter}
            onChange={(e) => setBarcodeFilter(e.target.value)}
            placeholder="Barkod tam"
            style={{ ...s.filterInput, width: 130, fontFamily: 'monospace', fontSize: 12 }}
          />
          <DateInput value={dateFrom} onChange={(v) => setDateFrom(v)} />
          <DateInput value={dateTo} onChange={(v) => setDateTo(v)} />
          <input
            type="search"
            value={searchText}
            onChange={(e) => { setSearchText(e.target.value); setPage(1); }}
            placeholder="Barkod / nokta / not / TN..."
            style={s.filterInput}
          />
        </div>
      </div>

      <StandardDataGrid
        rows={pagedRows}
        columns={columns}
        totalCount={totalCount}
        loading={loading}
        pageSize={pageSize}
        currentPage={page}
        onPageChange={setPage}
        onPageSizeChange={(n) => { setPageSize(n); setPage(1); }}
        sortField={sortField}
        sortDir={sortDir}
        onSortChange={(field, dir) => { setSortField(field); setSortDir(dir); }}
        selectedId={selectedId}
        onRowClick={(row) => setSelectedId(pick(row, 'id', 'Id'))}
        onRowDoubleClick={openDetail}
        exportFileName="cooler-arizalari"
      />

      {detailRow && (
        <div style={s.overlay} onClick={closeDetail}>
          <div style={{ ...s.modal, maxWidth: 760 }} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <h2 style={s.modalTitle}>
                {pick(detailRow, 'coolerBarcode', 'CoolerBarcode')} • Arıza
              </h2>
              <button type="button" style={s.closeBtn} onClick={closeDetail}>×</button>
            </div>
            <div style={s.modalBody}>
              <div style={s.formRow}>
                <div style={s.formGroup}>
                  <label style={s.label}>Nokta</label>
                  <div style={s.inputView}>{pick(detailRow, 'customerTitle', 'CustomerTitle') || '-'}</div>
                </div>
                <div style={s.formGroup}>
                  <label style={s.label}>Bildirim Zamanı</label>
                  <div style={s.inputView}>{formatDate(pick(detailRow, 'reportedAt', 'ReportedAt'))}</div>
                </div>
              </div>
              <div style={s.formRow}>
                <div style={s.formGroup}>
                  <label style={s.label}>Sebep</label>
                  <div style={s.inputView}>
                    {REASON_LABEL[pick(detailRow, 'reason', 'Reason')] || pick(detailRow, 'reason', 'Reason') || '-'}
                  </div>
                </div>
                <div style={s.formGroup}>
                  <label style={s.label}>Öncelik</label>
                  <div>{urgencyPillFor(pick(detailRow, 'urgency', 'Urgency'))}</div>
                </div>
                <div style={s.formGroup}>
                  <label style={s.label}>Etkilenen Kapı</label>
                  <div style={s.inputView}>
                    {pick(detailRow, 'affectedDoor', 'AffectedDoor') != null
                      ? `#${pick(detailRow, 'affectedDoor', 'AffectedDoor')}`
                      : 'Tüm cihaz'}
                  </div>
                </div>
              </div>
              {pick(detailRow, 'note', 'Note') && (
                <div style={s.formGroup}>
                  <label style={s.label}>Saha Notu</label>
                  <div style={s.textareaView}>{pick(detailRow, 'note', 'Note')}</div>
                </div>
              )}

              {/* Photo gallery */}
              {photoUrls.length > 0 && (
                <div style={s.formGroup}>
                  <label style={s.label}>Fotoğraflar ({photoUrls.length})</label>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {photoUrls.map((url, idx) => (
                      <a
                        key={idx}
                        href={url}
                        target="_blank"
                        rel="noreferrer"
                        style={{
                          display: 'block', width: 120, height: 90, borderRadius: 8,
                          border: '1px solid #e5e7eb', overflow: 'hidden',
                          background: '#f9fafb',
                        }}
                      >
                        <img
                          src={url}
                          alt={`foto ${idx + 1}`}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.parentElement.innerHTML =
                              `<div style="display:flex;align-items:center;justify-content:center;width:100%;height:100%;color:#94a3b8;font-size:11px;">Açılamadı</div>`;
                          }}
                        />
                      </a>
                    ))}
                  </div>
                </div>
              )}

              <div style={{
                marginTop: 8, padding: 14, borderRadius: 10,
                background: 'rgba(0, 115, 234, 0.04)', border: '1px solid rgba(0, 115, 234, 0.20)',
              }}>
                <div style={{ fontWeight: 800, fontSize: 13, marginBottom: 10, color: '#1d4ed8' }}>
                  Servis Yönetimi
                </div>
                <div style={s.formRow}>
                  <div style={s.formGroup}>
                    <label style={s.label}>Takip No</label>
                    <input
                      type="text"
                      value={editTrackingNo}
                      onChange={(e) => setEditTrackingNo(e.target.value)}
                      placeholder="örn. SRV-2026-0142"
                      style={{ ...s.input, fontFamily: 'monospace' }}
                    />
                  </div>
                  <div style={s.formGroup}>
                    <label style={s.label}>Durum</label>
                    <select
                      value={editStatus}
                      onChange={(e) => setEditStatus(e.target.value)}
                      style={s.select}
                    >
                      {STATUS_TABS.filter(t => t.key).map(t => (
                        <option key={t.key} value={t.key}>{t.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>
            <div style={s.modalFooter}>
              <button type="button" style={s.btnSecondary} onClick={closeDetail} disabled={saving}>
                Kapat
              </button>
              <button
                type="button"
                style={{ ...s.btnPrimary, opacity: saving ? 0.6 : 1 }}
                onClick={submitUpdate}
                disabled={saving}
              >
                {saving ? 'Kaydediliyor...' : 'Güncelle'}
              </button>
            </div>
          </div>
        </div>
      )}

      <Toast toast={toast} />
    </div>
  );
}

export default CoolerFaultsPageLegacy;
